import type { SelectOptionPropsType, SelectOptionType } from '@Shelly/shelly-design-system';
import { dateFormat } from 'src/helpers/date_helper';

export type TimeRate = [string, number];
export type DayPattern = Array<TimeRate | null>;

export type TariffTables = {
	days: DayPattern[];
	weeks: number[][];
	holidays: Array<[string, number] | null>;
	seasons: [number, number];
};

export const RATE_OPTIONS = ['T1', 'T2', 'T3', 'T4'];
export const MAX_PERIODS_PER_DAY = 6;
export const MAX_WEEK_PATTERNS = 3;

/**
 * Normalises a raw tariff tables payload into a fixed-size, UI-ready structure.
 * Pads day patterns to MAX_PERIODS_PER_DAY slots, drops sentinel week rows (all 255),
 * and guarantees at least one day/week pattern and exactly 15 holiday slots.
 */
export const normalizeTariffTables = (raw: TariffTables): TariffTables => {
	const days = (raw.days ?? []).map((pattern) => {
		const normalized: DayPattern = [];
		for (let i = 0; i < MAX_PERIODS_PER_DAY; i++) {
			normalized.push(pattern?.[i] ?? null);
		}
		return normalized;
	});
	if (days.length === 0) {
		days.push(Array(MAX_PERIODS_PER_DAY).fill(null));
	}

	const weeks = (raw.weeks ?? []).filter((w) => Array.isArray(w) && !w.every((v) => v === 255));
	if (weeks.length === 0) {
		weeks.push([0, 0, 0, 0, 0, 0, 0]);
	}

	const holidays: Array<[string, number] | null> = Array(15)
		.fill(null)
		.map((_, i) => raw.holidays?.[i] ?? null);

	const seasons: [number, number] = [raw.seasons?.[0] ?? 0, raw.seasons?.[1] ?? 0];

	return { days, weeks, holidays, seasons };
};

/**
 * Strips empty day patterns and re-indexes all week/holiday references before
 * sending to the device. Empty holiday slots and placeholder dates are excluded.
 * @param tables - The current in-memory tariff tables.
 * @returns A cleaned payload safe to pass to DaisyEM.SetTariffTables.
 */
export const buildPayload = (tables: TariffTables): TariffTables => {
	const activeOriginalIndices: number[] = [];
	tables.days.forEach((pattern, i) => {
		if (pattern.some(Boolean)) activeOriginalIndices.push(i);
	});

	const days = activeOriginalIndices.map((i) => tables.days[i].filter(Boolean));

	const remapDayIdx = (origIdx: number): number => {
		const mapped = activeOriginalIndices.indexOf(origIdx);
		return mapped >= 0 ? mapped : 0;
	};

	const weeks = tables.weeks.map((week) => week.map(remapDayIdx));

	const holidays = tables.holidays.filter(
		(h): h is [string, number] => Array.isArray(h) && h[0] !== '',
	);

	return { days, weeks, holidays, seasons: tables.seasons };
};

/**
 * Converts a period's numeric rate (1-based) to its display label (e.g. 2 → "T2").
 * Falls back to "T1" when the rate is out of range.
 * @param period - The [time, rate] tuple for a period slot.
 */
export const rateValue = (period: TimeRate): string => RATE_OPTIONS[(period[1] ?? 1) - 1] ?? 'T1';

/**
 * Select option matching a period's tariff rate (T1–T4), for translated rate labels.
 */
export const rateSelectOptionForPeriod = (
	options: SelectOptionPropsType[],
	period: TimeRate,
): SelectOptionPropsType => options.find((o) => o.value === rateValue(period)) ?? options[0];

/**
 * Select option for compressed day model D1… from storage index and active day slots.
 * @param storageDayIndex - Index into `tariffTables.days` as stored on week rows / holidays.
 */
export const dayModelSelectOptionForStorageIndex = (
	options: SelectOptionPropsType[],
	activeDayIndices: number[],
	storageDayIndex: number,
): SelectOptionPropsType => {
	const compressed = activeDayIndices.indexOf(storageDayIndex) + 1 || 1;
	const key = `D${compressed}`;
	return options.find((o) => o.value === key) ?? options[0];
};

/**
 * Select option for season schedule week model W1… by week-pattern index.
 */
export const weekModelSelectOptionAt = (
	options: SelectOptionPropsType[],
	weekPatternIndex: number,
): SelectOptionPropsType => options[weekPatternIndex] ?? options[0];

/**
 * Extracts the string value from a design-system Select change event.
 * @param e - The CustomEvent dispatched by the Select component.
 */
export const handleSelectChange = (e: CustomEvent<SelectOptionType>): string => {
	const detail = e.detail;
	if (typeof detail === 'string') return detail;
	if (detail && typeof detail === 'object' && 'value' in detail) return String(detail['value']);
	return String(detail);
};

/**
 * Extracts the string value from a design-system Input on:input event.
 * The Input component forwards the native DOM input event, so the value
 * lives on e.target rather than e.detail.
 * @param e - The Event dispatched by the Input component.
 */
export const handleInputChange = (e: Event): string => e.target?.['value'] ?? '';

/**
 * Parses a holiday date string into a Date object.
 * Accepts "YYYY-MM-DD" (full date) or "MM-DD" (current year assumed).
 * Returns null for empty, invalid, or non-string input.
 * @param dateStr - The date string stored in the tariff tables payload.
 */
export const parseHolidayDate = (dateStr: string | null | undefined): Date | null => {
	if (!dateStr || typeof dateStr !== 'string') return null;
	const parts = dateStr.split('-').map(Number);
	if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
		return new Date(parts[0], parts[1] - 1, parts[2]);
	}
	if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
		return new Date(new Date().getFullYear(), parts[0] - 1, parts[1]);
	}
	return null;
};

/**
 * Formats today's date as a "YYYY-MM-DD" string for use as a default holiday date.
 */
export const todayDateString = (): string => dateFormat(new Date(), 'Y-m-d');
